import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Gear } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppSettingsPage = () => {
  const { fetchSettings, appSettings: currentSettings } = useAuth();
  const [settings, setSettings] = useState({
    app_name: '',
    tagline: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/settings/app`);
      setSettings({
        app_name: data.app_name || '',
        tagline: data.tagline || ''
      });
    } catch (e) {
      console.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings/app`, settings);
      toast.success('Settings saved');
      fetchSettings();
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="h-96 skeleton rounded-md"></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold">App Settings</h1>
        <p className="text-muted-foreground">Configure application branding</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Gear size={20} className="text-primary" />
            Branding
          </CardTitle>
          <CardDescription>
            Customize your application name and tagline
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-4">
            <div className="space-y-2">
              <Label>Application Name</Label>
              <Input
                value={settings.app_name}
                onChange={(e) => setSettings({...settings, app_name: e.target.value})}
                placeholder="Branch Manager"
                data-testid="app-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Tagline</Label>
              <Input
                value={settings.tagline}
                onChange={(e) => setSettings({...settings, tagline: e.target.value})}
                placeholder="Insurance Operations Platform"
                data-testid="app-tagline-input"
              />
            </div>
            <Button type="submit" disabled={saving} data-testid="save-app-settings-btn">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Preview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-6 bg-secondary/50 rounded-md text-center">
            <h2 className="text-2xl font-bold">{settings.app_name || 'Branch Manager'}</h2>
            <p className="text-muted-foreground">{settings.tagline || 'Insurance Operations Platform'}</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AppSettingsPage;
